// Trail-Cache-Layer: holt GPX-Dateien vom Backend, simplifiziert sie,
// speichert in IndexedDB, und liefert GpxTrack-Objekte an die Map.

import { getDb, withDb } from './storage/db';
import { listTrails, fetchTrailGpx } from './api';
import { parseGpx, simplifyTrack, trackLength, elevationStats } from '@adtu/shared';
import type { CruxPoint, Difficulty, GpxTrack } from '@adtu/shared';

/**
 * Wave 14bl: MUSS mit useTrailElevation.ts SMOOTHING_VERSION übereinstimmen.
 * Sync lehnt Backend-Samples mit abweichender Version ab; der Client rechnet
 * dann sein eigenes Profil und pusht es zurück.
 *
 * Wave 14ce: bumped 1 → 2 zusammen mit useTrailElevation. Symmetrisches
 * Edge-Shrinking in smoothElevationSamples — alte Cache-/Server-Samples
 * haben asymmetrisch gedämpfte Endpunkte und werden ignoriert.
 */
const EXPECTED_SMOOTHING_VERSION = 2;
/** Sample-Count der Elevation-Pipeline (Client/Server identisch). */
const EXPECTED_SAMPLE_COUNT = 100;

/**
 * Custom-Event-Name, mit dem Komponenten außerhalb der MapPage (z. B. der
 * TabBar-Badge) erfahren, dass sich die Trail-Liste geändert hat. MapPage
 * feuert das Event nach syncTrailCache(). Hooks wie useTrailCount hören
 * darauf und re-fetchen den Count aus IDB.
 */
export const TRAILS_UPDATED_EVENT = 'adt:trails-updated';

/** Liefert die aktuelle Anzahl gecachter Trails (ohne sie alle zu lesen). */
export async function getCachedTrailCount(): Promise<number> {
  return withDb(async (db) => db.count('trails'));
}

export interface CachedTrail {
  id: string;
  name: string;
  difficulty: Difficulty;
  trailType: string;
  colorHex?: string | null;
  description?: string | null;
  photos?: string[];
  gpxFilename: string;
  track: GpxTrack;
  /** Admin-annotierte Schlüsselstellen (immer Array, ggf. leer). */
  cruxPoints: CruxPoint[];
  stats: { lengthMeters: number; gain: number; loss: number };
  cachedAt: string;
  /** Wave 14ca-fix64: bei Custom-Tour-Pseudo-Trails plumbing der per-
   *  segment-Trail-Mapping-Daten zum Trail-Detail-Sheet (fuer Trail-
   *  Badges + Höhenprofil-Faerbung). Nur gesetzt wenn der Trail ein
   *  Custom-Tour-Wrapper ist — undefined bei normalen Trails. */
  routeSegments?: Array<{
    kind: 'connector' | 'trail' | 'bridge';
    trailId: string | null;
    from: number;
    to: number;
  }>;
  /** Wave 14ca-fix64-v3: User-Auswahl beim Custom-Tour-Plannen. Whitelist
   *  fuer die Trail-Liste + Höhenprofil-Faerbung — Trails die zwar nahe
   *  der Route liegen aber NICHT vom User explizit angetappt wurden,
   *  werden im UI als Connector dargestellt. undefined = keine
   *  Whitelist (zeige alle erkannten Trails). */
  selectedTrailIds?: string[];
}

const VALID_DIFF = new Set([
  'uphill',
  'easy',
  'intermediate',
  'intermediate_plus',
  'hard',
  'expert',
  'extreme',
  'unknown',
] as const);
function normalizeDifficulty(v?: string | null): Difficulty {
  if (v && VALID_DIFF.has(v as Difficulty)) return v as Difficulty;
  return 'unknown';
}

// Fallback-Heuristik wenn Backend keine Metadaten liefert (alte Uploads)
function extractDifficultyFromName(name: string): Difficulty {
  const n = name.toLowerCase();
  // Uphill-Zustiege zuerst prüfen (spezifischer als Farb-Heuristiken).
  if (n.includes('uphill') || n.includes('zustieg') || n.includes('climb') || n.includes('yellow') || n.includes('gelb'))
    return 'uphill';
  if (n.includes('easy') || n.includes('grün') || n.includes('green')) return 'easy';
  // Reihenfolge wichtig: 'intermediate_plus' / 'intermediate+' müssen VOR
  // 'intermediate' geprüft werden — sonst matched das allgemeine Label zuerst.
  if (n.includes('intermediate+') || n.includes('intermediate_plus') || n.includes('lila') || n.includes('purple'))
    return 'intermediate_plus';
  if (n.includes('intermediate') || n.includes('blau') || n.includes('blue')) return 'intermediate';
  if (n.includes('extreme') || n.includes('striped')) return 'extreme';
  if (n.includes('expert') || n.includes('double') || n.includes('black')) return 'expert';
  if (n.includes('hard') || n.includes('rot') || n.includes('red')) return 'hard';
  return 'unknown';
}

/**
 * Gleicher Shape wie CachedTrail — extrahiert aus IDB-Row-Map damit
 * Initial-Emit + Remote-Emit denselben Weg gehen und die Karte/UI auf
 * eine einheitliche Struktur reagieren kann.
 */
function toCachedTrail(t: {
  id: string;
  name: string;
  difficulty: string;
  trailType: string;
  colorHex?: string | null;
  description?: string | null;
  photos?: string[];
  gpxFilename: string;
  track: GpxTrack;
  cruxPoints?: CruxPoint[];
  cachedAt: string;
}): CachedTrail {
  return {
    id: t.id,
    name: t.name,
    difficulty: normalizeDifficulty(t.difficulty),
    trailType: t.trailType,
    colorHex: t.colorHex ?? null,
    description: t.description ?? null,
    photos: t.photos ?? [],
    gpxFilename: t.gpxFilename,
    track: t.track,
    cruxPoints: Array.isArray(t.cruxPoints) ? t.cruxPoints : [],
    // Wave 14bk: 2m Deadband beim Stats-Count — ohne den summiert sich
    // GPS-ele-Noise zu fantastischen ↑/↓-Werten auf (z.B. flacher Küstenweg
    // mit 80m↑80m↓ obwohl er de facto null Höhenmeter macht). Die Chart-
    // Samples im UI werden zusätzlich moving-average-geglättet, da reicht
    // 1m Residual-Deadband (siehe MapPage.derivedStats).
    stats: {
      lengthMeters: trackLength(t.track.points),
      ...elevationStats(t.track.points, { deadbandMeters: 2 }),
    },
    cachedAt: t.cachedAt,
  };
}

/**
 * Max parallele HTTP-Fetches für GPX-Dateien. Browsers serialisieren eh auf
 * ~6 pro HTTP/1.1-Origin; HTTP/2 kann deutlich mehr. 8 ist ein Kompromiss:
 * ausreichend Pipeline-Parallelität, ohne den Server bei 28+ Trails mit
 * einem Burst zu erschlagen.
 */
const SYNC_CONCURRENCY = 8;

/**
 * Lädt die Liste der Trails vom Backend und synchronisiert in IndexedDB.
 * Gibt alle aktuell gecachten Trails zurück (inkl. unberührter alter Einträge
 * falls Backend unreachable).
 *
 * Wave 14ax — Progressive Loading:
 *   - Initial-Emit: alles was schon im IDB liegt, wird sofort via `onProgress`
 *     gemeldet, damit die Map gar nicht erst auf den Backend-Call wartet.
 *   - Parallel-Fetch: Remote-Trails werden mit SYNC_CONCURRENCY gleichzeitig
 *     gezogen statt 28× sequenziell (vorher: 30-60 s Main-Thread-Stau, der
 *     sogar MapLibre's `load`-Event starven liess).
 *   - Per-Trail-Emit: sobald ein Trail frisch gecached ist, feuert `onProgress`.
 *     MapPage akkumuliert das in State und re-rendert → User sieht Trails
 *     inkrementell erscheinen statt 60 s schwarzen Screen.
 */
export async function syncTrailCache(opts: {
  domParser: DOMParser;
  onProgress?: (trail: CachedTrail) => void;
}): Promise<CachedTrail[]> {
  const db = await getDb();

  // 1) Was im IDB liegt sofort an die UI melden — besonders wichtig für
  //    Offline-Starts und wiederholte Page-Mounts. Die Map bleibt nicht
  //    mehr leer, während wir auf /api/trails warten.
  if (opts.onProgress) {
    try {
      const preloaded = await db.getAll('trails');
      for (const t of preloaded) opts.onProgress(toCachedTrail(t));
    } catch {
      // IDB-Leseprobleme sind nicht tödlich — weitermachen.
    }
  }

  // 2) Aktuelle Liste vom Backend. Offline → nur lokalen Cache zurückgeben.
  let remote: Awaited<ReturnType<typeof listTrails>> | null = null;
  try {
    remote = await listTrails();
  } catch {
    // offline — ok
  }

  if (remote) {
    // 3) Pro-Trail-Arbeit. Entscheidet Cache-Skip, sonst fetch+parse+store+emit.
    const syncOne = async (t: (typeof remote.trails)[number]): Promise<void> => {
      const id = t.filename.replace(/\.gpx$/, '');
      const existing = await db.get('trails', id);

      // Wave 14cf: pending-crux-Schutz.
      // Wenn der lokale Trail noch eine pending Crux-Edit-Queue-Eintrag hat
      // (= User hat eine Schlüsselstelle markiert, der Server hat sie aber
      // noch nicht), darf der Server-Stand die lokale cruxPoints NICHT
      // überschreiben — sonst sieht der User nach jedem App-Reload seine
      // gerade markierte Schlüsselstelle weg. flushCruxQueue() läuft im
      // App-Root und pushed die Pendings asynchron; bis dahin gilt local.
      let pendingCruxLocal = false;
      try {
        const pendingRow = await db.get('cruxQueue', id);
        if (pendingRow) pendingCruxLocal = true;
      } catch {
        /* IDB-Quirk → defensiv keine Annahme treffen */
      }

      // Re-Sync wenn: keine Existenz, oder Backend-Update neuer, oder Metadaten
      // unterschiedlich. cruxPoints-Deep-Compare via JSON.stringify — kleine
      // Arrays, deep-equal via string ist einfacher und für das Volumen OK.
      // Bei pending-crux: cruxPoints-Diff IGNORIEREN — local-truth gewinnt.
      const cruxJsonLocal = JSON.stringify(existing?.cruxPoints ?? []);
      const cruxJsonRemote = JSON.stringify(t.cruxPoints ?? []);
      const cruxesDiffer = !pendingCruxLocal && cruxJsonLocal !== cruxJsonRemote;
      const metaChanged =
        existing &&
        (existing.difficulty !== normalizeDifficulty(t.difficulty) ||
          existing.colorHex !== (t.colorHex ?? null) ||
          existing.description !== (t.description ?? null) ||
          existing.trailType !== (t.trailType ?? 'downhill') ||
          existing.name !== (t.name ?? existing.name) ||
          cruxesDiffer);
      if (existing && existing.cachedAt >= t.updatedAt && !metaChanged) return;

      try {
        const xml = await fetchTrailGpx(t.filename);
        const parsedTracks = parseGpx(xml, { domParser: opts.domParser });
        if (parsedTracks.length === 0) return;
        const track = parsedTracks[0]!;
        // Aggressive Simplification für offline: 4m Toleranz reicht für MTB-Nav
        const simplified: GpxTrack = {
          name: track.name,
          points: simplifyTrack(track.points, 4),
        };
        const name = t.name || track.name || id;
        // Backend-Difficulty hat Vorrang, dann Name-Heuristik, dann 'unknown'
        const difficulty = t.difficulty
          ? normalizeDifficulty(t.difficulty)
          : extractDifficultyFromName(name);
        // Wave 14cf: bei pending-crux NICHT überschreiben mit Server-Stand —
        // lokale Cruxes warten noch auf Push, dürfen nicht weggeworfen werden.
        // Andere Felder (name, difficulty, gpx, etc) sind unkritisch und
        // dürfen vom Server gewinnen.
        const effectiveCruxPoints =
          pendingCruxLocal && existing
            ? existing.cruxPoints ?? []
            : Array.isArray(t.cruxPoints)
              ? t.cruxPoints
              : [];
        const record = {
          id,
          name,
          difficulty,
          trailType: t.trailType || 'downhill',
          colorHex: t.colorHex ?? null,
          description: t.description ?? null,
          photos: t.photos ?? [],
          gpxFilename: t.filename,
          gpxRaw: xml,
          track: simplified,
          cruxPoints: effectiveCruxPoints,
          cachedAt: t.updatedAt,
        };
        await db.put('trails', record);
        if (opts.onProgress) opts.onProgress(toCachedTrail(record));

        // Wave 14bl: Backend-gelieferte Elevation-Samples spiegeln.
        //
        // Wenn der Server beim Upload ein Profil mit opentopodata vorab
        // gesampled hat (oder ein früherer Rider eins gepusht hat), kommt
        // das Array als `elevationSamples` in der Trail-Liste mit. Wir
        // spiegeln es in den `trailElevations`-IDB-Store — genau denselben
        // Slot den `useTrailElevation` abfragt — und der allererste Besuch
        // auf der Detail-/Map-Seite rendert das Profil sofort, ohne Extra-
        // Roundtrip, auch offline.
        //
        // Strikte Versionsprüfung: wenn der Server eine andere Smoothing-
        // Version spricht (sollte dank synchron-deployter Konstante nie
        // passieren, aber defense-in-depth), ignorieren wir — der Client
        // rechnet sein eigenes Profil und pusht es zurück.
        if (
          Array.isArray(t.elevationSamples) &&
          t.elevationSamples.length === EXPECTED_SAMPLE_COUNT &&
          t.elevationSmoothingVersion === EXPECTED_SMOOTHING_VERSION &&
          typeof t.elevationTrackLengthMeters === 'number' &&
          Number.isFinite(t.elevationTrackLengthMeters)
        ) {
          // Sanity: alle Werte endlich.
          let allFinite = true;
          for (const v of t.elevationSamples) {
            if (!Number.isFinite(v)) {
              allFinite = false;
              break;
            }
          }
          if (allFinite) {
            try {
              // Existierenden Eintrag nicht "verschlechtern": wenn die lokale
              // Version bereits mit passender smoothingVersion und ähnlicher
              // trackLength im Store liegt, belassen (könnte aus einem
              // direkten MapLibre-Sample auf genau diesem Client stammen).
              // Server-Sample überschreibt nur wenn nichts da ist oder der
              // Eintrag stale ist.
              const existingElev = await db.get('trailElevations', id);
              const shouldWrite =
                !existingElev ||
                existingElev.smoothingVersion !== EXPECTED_SMOOTHING_VERSION ||
                Math.abs(
                  (existingElev.trackLengthMeters ?? 0) -
                    (t.elevationTrackLengthMeters ?? 0),
                ) > 50;
              if (shouldWrite) {
                await db.put('trailElevations', {
                  trailId: id,
                  samples: t.elevationSamples,
                  source: 'srtm',
                  computedAt: new Date().toISOString(),
                  trackLengthMeters: t.elevationTrackLengthMeters,
                  smoothingVersion: EXPECTED_SMOOTHING_VERSION,
                });
              }
            } catch (err) {
              console.warn('[trails] elevation mirror failed', id, err);
            }
          }
        }
      } catch (err) {
        console.warn('Failed to cache trail', t.filename, err);
      }
    };

    // 4) Concurrency-Pool: bis SYNC_CONCURRENCY parallel. Statt strikt
    //    sequenziell (28 × await) → worker-queue Pattern. Bei flakigem
    //    Netz bleibt die Pipeline stabil, weil Fehler in einem Worker
    //    die anderen nicht blockieren.
    const queue = [...remote.trails];
    const workers = Array.from(
      { length: Math.min(SYNC_CONCURRENCY, queue.length) },
      async () => {
        while (queue.length > 0) {
          const t = queue.shift();
          if (!t) return;
          await syncOne(t);
        }
      },
    );
    await Promise.all(workers);

    // 5) Wave 14bz — Deletes durchreichen.
    //
    // Wenn ein Admin auf dem Server Trails gelöscht hat, sind sie nicht
    // in `remote.trails`. Vorher haben wir dann nur nicht mehr geupdated,
    // den IDB-Eintrag aber stehen lassen — User sah den Trail weiter auf
    // der Karte und im List-View bis er die PWA rekonfiguriert.
    //
    // Jetzt: alle IDB-Einträge, deren id nicht im Remote-Set vorkommt,
    // werden gelöscht (inkl. zugehöriger Elevation-Samples).
    //
    // WICHTIG: nur ausführen wenn remote SUCCESS war. Offline-Start
    // darf den Cache nicht wischen — sonst hat der User beim nächsten
    // Offline-Rad keine Karte mehr.
    const remoteIds = new Set(
      remote.trails.map((t) => t.filename.replace(/\.gpx$/, '')),
    );
    const allLocal = await db.getAll('trails');
    for (const local of allLocal) {
      if (!remoteIds.has(local.id)) {
        try {
          await db.delete('trails', local.id);
          // Elevation-Samples-Leiche auch wegräumen.
          await db.delete('trailElevations', local.id);
        } catch (err) {
          console.warn('[trails] stale delete failed', local.id, err);
        }
      }
    }
  }

  const all = await db.getAll('trails');
  return all.map(toCachedTrail);
}
